<template id="SignUp">
    <section class="padded-container">
        <div class="sign-up-grid" :aa-region="formConfig.aaRegion">
            <div class="grid-item">
                <steps-header />

                <p v-if="getSubHeading" class="sub-heading">
                    {{ getSubHeading }}
                </p>
                
                <p
                    v-if="formMessageError"
                    role="alert"
                    class="form-message error"
                >
                    {{ formMessageError }}
                </p>
            </div>

            <form
                id="sign-up-form"
                v-on:submit.stop.prevent="handleSubmit"
                novalidate
            >
                <FormFields
                    :formFieldList="formFieldList"
                    :class="classList"
                    @submitValidate="handleSubmit"
                    :fieldErrors="fieldErrors"
                    @updateFormData="updateFormData"
                    @validateField="validateField"
                    @validateForm="validateForm"
                    @trackCheckBox="trackCheckBox"
                    @showHidePasswordStatus="handleShowHidePasswordStatus"
                ></FormFields>

                <div
                    class="disclaimer"
                    v-if="
                        !formFieldList['disclaimer'] &&
                        getFieldLabel('disclaimer') &&
                        !this.overrideRequiredAgreementCheckboxEnabled()
                    "
                    v-html="getFieldLabel('disclaimer')"
                ></div>

                <FormButton
                    :buttonConfig="buttonConfig"
                    :buttonState="submitButtonState"
                    class="button"
                    @click.native="handleSubmit"
                ></FormButton>
            </form>
        </div>
        <BackToRendezvousOptions
            :separator="false"
            v-if="!isDomestic"
        ></BackToRendezvousOptions>

    </section>
</template>

<script>
    import BackToRendezvousOptions from 'aa/vue/components/BackToRendezvousOptions';
    import ButtonCta from 'atoms/ButtonCta';
    import CheckboxElm from 'atoms/CheckboxElm';
    import CustomSelect from 'atoms/CustomSelect';
    import Domain from 'helpers/Domain';
    import FormButton from 'atoms/FormButton';
    import FormFields from 'atoms/FormFields';
    import FormState from 'aa/vue/plugins/FormState';
    import InputElm from 'atoms/InputElm';
    import LabelInput from 'atoms/LabelInput';
    import LabelInputBirthDate from 'atoms/LabelInputBirthDate';
    import MVPDBindMixin from 'aa/vue/plugins/MVPDBindMixin';
    import StepsHeader from 'aa/vue/components/StepsHeader';
    import UrlHelper from 'helpers/UrlHelper';
    import isEqual from 'lodash/isEqual';
    import {
        AA_XHR_BASE_PATH
    } from 'aa/vue/constants/aaRoutes';
    import { SignUpConfig } from 'aa/vue/FormConfig';
    import { getMVPDAuthZObj } from 'helpers/mvpdStorage';
    import {mapState, mapActions, mapGetters} from 'vuex';
    import {
        tracking,
        ACTION_NAMES,
        APP_LOG_TYPE,
        PAGE_NAMES,
        PAGE_TYPES
    } from 'aa/vue/services/Tracking';
    import PartnerMixin from './partner/PartnerMixin';

    import { AGE_GATE } from 'aa/helpers/featureConstants';
    import moment from 'moment';
    import { toInteger } from 'lodash';

    export default {
        props: {
            errorCallBack: {
                type: Function
            },
            successCallback: {
                type: Function
            },
            ageGateCallback: {
                type: Function,
                required: false,
            },
            fromConfirmation: {
                type: Boolean,
                default: () => false
            },
            fromPartnerConfirmation: {
                type: Boolean,
                default: () => false
            }
        },
        mixins: [
            FormState, 
            MVPDBindMixin, 
            PartnerMixin,
        ],
        watch: {
            authSuiteTokens(newValue, oldValue) {
                if (this.fromConfirmation) {
                    if (
                        this.tokenStatus === 'valid' &&
                        !isEqual(newValue, oldValue)
                    ) {
                        this.registerWithApi(this.authSuiteTokenErrorCallBack);
                    }
                }
            },
            pageAttr() {
                this.setFields();
            },
        },
        data: function () {
            let classList = ['form-wrapper'];
            if (Domain.isInternational()) {
                classList.add('form-wrapper--full');
            }
            return {
                heading: 'Sign Up',
                form: {},
                classList: classList,
                tnc: null,
                errors: [],
                buttonConfig: Object.assign(SignUpConfig.buttonConfig, {
                    event: this.handleSubmit
                }),
                formMessageError: '',
                formName: 'aa_sign_up',
                marketingCheckBox: false,
                tcConsentStatus: null,
                mktConsentStatus: null,
                isDomestic: Domain.isDomestic(),
                marketingConsent: null,
                passIsVisible: 0,
                recaptchaAction: 'FORM_SIGN_UP',
            };
        },
        mounted() {
            // TODO: this.$el is undefined, fix it!
            if (this.$el) {
                const element = this.$el.querySelector('input[tabindex="1"]');
                if (element) {
                    element.focus();
                }
            }
            
        },
        methods: {
            ...mapActions('authSuite', {
                checkAccessStatus: 'checkAccessStatus',
                registerWithApi: 'registerWithApi'
            }),
            ...mapActions('partner', {
                bindWithPartner: 'bindWithPartner'
            }),
            ...mapActions('partner', {
                bindWithPartner: 'bindWithPartner'
            }),
            handleShowHidePasswordStatus(payload) {
                this.passIsVisible = Number(payload)
                tracking.trackAction(
                    this.passIsVisible ?
                        ACTION_NAMES.SHOWHIDE_PASSWORD_SHOW :
                        ACTION_NAMES.SHOWHIDE_PASSWORD_HIDE,
                    {
                        passIsVisible: this.passIsVisible.toString()
                    }
                )
            },
            inCurrentCountry(countryList) {
                return countryList.indexOf(
                    this.marketingCheckBox.currentCountry
                ) > -1
                    ? true
                    : false;
            },
            isUnderAge() {
                // Birthday is stored without slashes or spaces, so we need to split it up
                const resp = RegExp('([0-9]{2})([0-9]{2})([0-9]{4})').exec(this.formData.birthday)
                if (!resp || resp.length < 4) return false
                const age_limit = this.$root.serverData?.ageGateMinimumAge
                // Preferred formt for moment() is Y-M-D
                const diff = moment().diff(resp[3] + '-' + resp[1] + '-' + resp[2], 'years', true)
                return diff < toInteger(age_limit);
            },
            trackCheckBox(value, e) {
                this.marketingConsent = value && Domain.isDomestic() ? '1' : '0';
                if (Domain.isInternational()) {
                    let trackingType;
                    if (e === 'optIn') {
                        this.mktConsentStatus = this.getMktConsentStatus(value);
                        trackingType = value
                            ? ACTION_NAMES.MARKETING_CHECKBOX_SELECT
                            : ACTION_NAMES.MARKETING_CHECKBOX_DESELECT;
                    } else if (e === 'requiredAgreement') {
                        trackingType = value
                            ? ACTION_NAMES.TC_CHECKBOX_SELECT
                            : ACTION_NAMES.TC_CHECKBOX_DESELECT;
                    }
                    tracking.trackAction(trackingType, {
                        pageType: PAGE_TYPES.SIGNUP,
                        pageName: PAGE_NAMES.SIGNUP,
                        pageUrl: window.location.href
                    });
                }
            },

            overrideOptIn() {
                if (
                    this.inCurrentCountry(
                        this.marketingCheckBox.marketing_optin_countries_1
                    )
                ) {
                    return this.marketingCheckBox.marketing_optin_copy_1;
                } else if (
                    this.inCurrentCountry(
                        this.marketingCheckBox.marketing_optin_countries_2
                    )
                ) {
                    return this.marketingCheckBox.marketing_optin_copy_2;
                } else {
                    return false;
                }
            },
            overrideOptInCheckboxState() {
                if (
                    this.inCurrentCountry(
                        this.marketingCheckBox.marketing_optin_checked
                    )
                ) {
                    return true;
                } else {
                    return false;
                }
            },
            getMarketCheckBox() {
                if (this.marketingCheckBox) {
                    return {
                        tcConsentStatus: this.tcConsentStatus,
                        mktConsentStatus: this.mktConsentStatus
                    };
                }

                return false;
            },
            getTcConsentStatus() {
                if (this.marketingCheckBox) {
                    return this.overrideRequiredAgreementCheckboxEnabled()
                        ? 1
                        : 2;
                }
            },
            getMktConsentStatus(value) {
                if (this.marketingCheckBox) {
                    if (this.overrideOptInCheckboxState()) {
                        return value ? 1 : 2;
                    } else {
                        return value ? 3 : 4;
                    }
                }
            },
            overrideRequiredAgreementCheckboxEnabled() {
                if (
                    this.marketingCheckBox &&
                    this.inCurrentCountry(
                        this.marketingCheckBox.terms_checkbox_enabled
                    )
                ) {
                    return true;
                } else {
                    return false;
                }
            },

            overrideRequiredAgreement() {
                return this.marketingCheckBox.terms_copy;
            },
            setFields() {
                const form = {
                    ...this.$root.serverData.signUpForm
                };

                const pageAttributes =
                    this.$store.getters['pageAttributes/getAllPageAttributes'];
                this.marketingCheckBox =
                    this.$root.serverData.marketingCheckBoxAttributes;
                if (this.marketingCheckBox) {
                    form.optIn.checked = this.overrideOptInCheckboxState();
                    pageAttributes.aa_sign_up_requiredagreement =
                        this.overrideRequiredAgreementCheckboxEnabled();
                    this.mktConsentStatus = this.getMktConsentStatus(
                        form.optIn.checked ? 1 : null
                    );
                    this.tcConsentStatus = this.getTcConsentStatus();
                }
                this.marketingConsent = form?.optIn?.checked && Domain.isDomestic() ? '1': '0';
                if (
                    form.requiredAgreement &&
                    !pageAttributes.aa_sign_up_requiredagreement
                ) {
                    delete form.requiredAgreement;
                }
                if (
                    this.$store.getters['flow/isEdu'] &&
                    this.$store.getters['user/eduProfile']
                ) {
                    let eduFormValues = this.$store.getters['user/eduProfile'];
                    form.fullName.value = eduFormValues.fullName;
                    form.email = Object.assign(form.email, {
                        value: eduFormValues.email,
                        readonly: 'readonly'
                    });
                    form.birthday.value = eduFormValues.birthdate;
                }
                this.setFieldConfigs(form);
                this.setValidationService();
            },
            getFieldLabel: function (fieldName) {
                // TODO: move to mixin SubFlowForm
                if (this.marketingCheckBox) {
                    if (fieldName === 'optIn') {
                        let overrideOptIn = this.overrideOptIn();

                        if (overrideOptIn) {
                            return overrideOptIn;
                        }
                    }
                    if (
                        fieldName === 'requiredAgreement' ||
                        fieldName === 'disclaimer'
                    ) {
                        let overrideRequiredAgreement =
                            this.overrideRequiredAgreement();

                        if (overrideRequiredAgreement) {
                            return overrideRequiredAgreement;
                        }
                    }
                }

                if (
                    this.pageAttr[this.formName + '_' + fieldName.toLowerCase()]
                ) {
                    return this.pageAttr[
                        this.formName + '_' + fieldName.toLowerCase()
                    ];
                }
            },
            authSuiteTokenErrorCallBack: function () {
                this.checkAccessStatus();
            },
            
            handleValidForm: async function () {
                if (this.buttonConfig.styleClass.indexOf('disabled') >= 0) {
                    return;
                }
                this.buttonConfig.styleClass.push('disabled');

                const http = this.$store.$http;
                const httpData = Object.assign({}, this.formData);

                /* Special Repair to Gender Value */
                /* Remove when CustomSelect emits input value */
                if (httpData.gender) {
                    httpData.gender = httpData.gender.split(' ').join('_');
                }

                // Add recaptcha action in the httpData
                if (this.hasOwnProperty('recaptchaAction')) {
                    httpData.recaptchaAction = this.recaptchaAction;
                }

                httpData.tk_trp = this.$store.state.token;

                const context = this;

                if (Domain.isDomestic()) {
                    try {
                        await this.unbindMVPDIfNeeded();
                    } catch (e) {
                        return;
                    }
                }
                
                http.doPost(`${AA_XHR_BASE_PATH}register/`, httpData).then(
                    async (res) => {
                        if (res.success) {
                            this.$store.commit(
                                'user/parseUserData',
                                res.data.user
                            );
                            // Track registration.
                            tracking.trackRegistration(
                                res.data.user,
                                this.$store.getters['flow/userRegType'],
                                this.$store.getters['flow/type'],
                                this.$store.getters['flow/code'],
                                this.getMarketCheckBox(),
                                this.marketingConsent,
                                this.passIsVisible.toString(),
                            );

                            context.formMessageError = '';

                            let flowType = this.$store.getters['flow/type'];

                            if (Domain.isDomestic() && getMVPDAuthZObj()) {
                                try {
                                    await this.bindMVPD();
                                } catch (e) {
                                    return;
                                }
                            }

                            if (this.fromConfirmation) {
                                this.registerWithApi(
                                    this.authSuiteTokenErrorCallBack
                                );
                            } else if (this.fromPartnerConfirmation) {
                                this.bindWithPartner().then((response) => {
                                    if (response?.success === false) {
                                        this.showPartnerError(
                                            response?.errorCode
                                        );
                                    }
                                });
                            } else if (flowType === 'f-gift') {
                                context.$router.push(this.getNextStep.path);
                            } else {
                                if (
                                    typeof context.$options.propsData
                                        .successCallback !== 'undefined'
                                ) {
                                    context.$options.propsData.successCallback(
                                        context
                                    );
                                } else if (
                                    context.$store.getters['flow/skipPayment']
                                ) {
                                    window.location.href =
                                        context.$store.getters[
                                            'flow/destinationUrl'
                                        ];
                                } else {
                                    context.$router.push({
                                        name: 'PAYMENT'
                                    });
                                }
                            }
                        } else {

                            tracking.trackAction(ACTION_NAMES.APP_LOG, {
                                appLogText:
                                    res.message || 'No message provided',
                                appLogType: APP_LOG_TYPE.SIGN_UP_ERROR,
                                appLogCode: 'No code provided',
                                passIsVisible: this.passIsVisible.toString()
                            });
                            context.buttonConfig.styleClass =
                                context.buttonConfig.styleClass.filter(
                                    (cssClass) => cssClass !== 'disabled'
                                );
                            context.formMessageError =
                                typeof res.message !== 'undefined' &&
                                res.message !== '' &&
                                res.message !== 'no error provided' &&
                                res.message !== 'VALIDATION_FAILED'
                                    ? res.message
                                    : this.$getLocale(
                                          'account_creation_error',
                                          {}
                                      );
                            
                            if (
                                typeof context.$options.propsData
                                    .errorCallback !== 'undefined'
                            ) {
                                context.$options.propsData.errorCallback();
                            } else if (res.errors) {
                                this.setFieldErrors(res.errors);
                                context.formMessageError = '';
                            }
                        }
                    }
                );
            },
            textInputErrorCSS: (key) => {
                return 'text-input-error qt-err-' + key;
            },
            
        },
        computed: {
            ...mapGetters({
                featureIsActive: 'featureFlags/isActive'
            }),
            
            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes
            }),
            ...mapState('flow', {
                flow: (state) => state.config
            }),
            ...mapState({
                authSuiteTokens: (state) => state['authSuite']?.tokens,
                tokenStatus: (state) => state['authSuite']?.tokenStatus
            }),
            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes
            }),
            hasAgeGate() {
                return this.featureIsActive(AGE_GATE)
            },
            getSubHeading() {
                return this.pageAttr.aa_sign_up_sub_heading
            },
            
            isInError() {
                return (key) => {
                    if (this.errors.length === 0) {
                        return false;
                    }
                    const result = this.errors.filter(
                        (err) => typeof err[key] !== 'undefined'
                    );
                    return result.length !== 0;
                };
            },
            formConfig() {
                return SignUpConfig;
            },

            currentPlan: function () {
                return this.$store.getters['plan/getCurrentPlan'];
            },

            getNextStep: function () {
                return this.$store.getters['flow/getNextStep'];
            }
        },
        filters: {
            valuesFromObj: (objStr) => {
                return Object.values(objStr).toString();
            }
        },
        components: {
            StepsHeader,
            ButtonCta,
            InputElm,
            FormFields,
            FormButton,
            LabelInput,
            CustomSelect,
            CheckboxElm,
            LabelInputBirthDate,
            BackToRendezvousOptions,
        },

        // Lifecycle Hooks
        created() {
            this.setFields();
        },
        updated() {
            const button = document.querySelector('.button__close');
            if (button) {
                button.focus();
            }
        }
    };
</script>
